import {
  ReactElement,
  ReactNode,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import clsx from 'clsx';
import { FormApi, Mutator } from 'final-form';
import arrayMutators from 'final-form-arrays';
import isEqual from 'lodash/isEqual';
import { Form, FormProps, FormRenderProps } from 'react-final-form';

import { SxProps, Box } from '@cast/design-system';

import { RffFormContext } from './_providers';
import { FormMode } from './types';

export type RffFormRef<
  InitialFormValues extends Record<string, any>,
  FormValues = InitialFormValues
> = {
  form: FormApi<FormValues, InitialFormValues>;
};

type Props<
  InitialFormValues extends Record<string, any>,
  FormValues = InitialFormValues
> = Omit<
  FormProps<FormValues>,
  'initialValuesEqual' | 'initialValues' | 'mutators'
> & {
  ref?: Ref<RffFormRef<InitialFormValues, FormValues>>;
  children?: ReactNode | ((form: FormRenderProps<FormValues>) => ReactNode);
  array?: boolean;
  mode?: FormMode;
  mutators?: Record<string, Mutator<FormValues, InitialFormValues>>;
  initialValues: InitialFormValues;
  initialValuesEqual?: (
    a?: InitialFormValues,
    b?: InitialFormValues
  ) => boolean;
  onDirty?: (isDirty: boolean) => void;
  onValidate?: (isValid: boolean) => void;
  sx?: SxProps;
  testId?: string;
  submitting?: boolean;
};

type FormRendererProps<
  InitialFormValues extends Record<string, any>,
  FormValues = InitialFormValues
> = Pick<
  Props<InitialFormValues, FormValues>,
  'ref' | 'onDirty' | 'onValidate' | 'children' | 'testId' | 'sx' | 'submitting'
> & {
  formProps: FormRenderProps<FormValues, InitialFormValues>;
  mode: FormMode;
};

const FormRenderer = <
  InitialFormValues extends Record<string, any>,
  FormValues = InitialFormValues
>({
  ref,
  formProps,
  onDirty,
  onValidate,
  children,
  mode,
  submitting,
  sx,
  testId,
}: FormRendererProps<InitialFormValues, FormValues>): ReactElement => {
  const dirtyRef = useRef<boolean>(undefined);
  const validRef = useRef<boolean>(undefined);

  const formRef = useRef<RffFormRef<InitialFormValues, FormValues>>(
    undefined as never
  );
  useImperativeHandle(ref, () => formRef.current);

  const { handleSubmit, ...rest } = formProps;

  useEffect(() => {
    if (dirtyRef.current !== formProps.dirty) {
      onDirty?.(formProps.dirty);
      dirtyRef.current = formProps.dirty;
    }
  }, [formProps.dirty, onDirty, dirtyRef]);

  useEffect(() => {
    if (validRef.current !== formProps.valid) {
      onValidate?.(formProps.valid);
      validRef.current = formProps.valid;
    }
  }, [formProps.valid, onValidate, validRef]);

  if (formRef.current == null) {
    formRef.current = {
      form: rest.form,
    } as unknown as RffFormRef<InitialFormValues, FormValues>;
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={sx}
      data-testid={testId}
      className={clsx('RffForm-root', formProps.dirty && 'RffForm-dirty')}
    >
      <RffFormContext.Provider
        value={{ mode, submitting: submitting ?? formProps.submitting }}
      >
        {typeof children === 'function' ? children(formProps as any) : children}
      </RffFormContext.Provider>
    </Box>
  );
};

export const RffForm = <
  InitialFormValues extends Record<string, any>,
  FormValues = InitialFormValues
>({
  ref,
  children,
  mutators = {},
  array,
  mode = 'create',
  onDirty,
  onValidate,
  sx,
  testId,
  submitting,
  initialValuesEqual = isEqual,
  ...formProps
}: Props<InitialFormValues, FormValues>): ReactElement => {
  return (
    <Form<FormValues, InitialFormValues>
      render={(formRenderProps) => (
        <FormRenderer
          ref={ref}
          formProps={formRenderProps}
          onDirty={onDirty}
          onValidate={onValidate}
          mode={mode}
          submitting={submitting}
          sx={sx}
          testId={testId}
        >
          {children}
        </FormRenderer>
      )}
      mutators={array ? { ...arrayMutators, ...mutators } : { ...mutators }}
      initialValuesEqual={initialValuesEqual}
      {...(formProps as any)}
    />
  );
};
